import * as React from "react";
import {
  Field,
  Input,
  Textarea,
  Radio,
  RadioGroup,
  Button,
  Spinner,
  Body1,
  Caption1,
  Checkbox,
  tokens,
  makeStyles,
} from "@fluentui/react-components";
import { ArrowLeft24Regular, Send24Filled } from "@fluentui/react-icons";

import { EntityDropdown } from "./EntityDropdown";
import { CategoryDropdown } from "./CategoryDropdown";
import { TechnicianDropdown } from "./TechnicianDropdown";
import { RequesterPicker } from "./RequesterPicker";
import { GenericTaskBlock, GenericTaskValue, defaultTaskValue } from "./GenericTaskBlock";
import { AttachmentsBlock } from "./AttachmentsBlock";
import { TicketStatusDropdown } from "./TicketStatusDropdown";
import { ErrorBar } from "./ErrorBar";

import { useEntities } from "../hooks/useEntities";
import { useCategories } from "../hooks/useCategories";
import { useTechnicians } from "../hooks/useTechnicians";

import { getGlpiClient } from "../services/glpiClient";
import { getMailContext, getAttachmentBlob, MailContext } from "../services/outlookContext";
import {
  stripHtml,
  prepareMailHtmlForGlpi,
  buildTicketBodyContent,
} from "../services/htmlStripper";
import { buildTicketTaskPayloads } from "../services/ticketTaskPayload";
import { TICKET_STATUS_LABELS } from "../types/glpiConstants";
import {
  GlpiUser,
  TicketStatus,
  TicketType,
  CreateTicketPayload,
} from "../types/glpi";
import { buildTicketWebUrl } from "../types/glpiConstants";

export interface CreatedTicketInfo {
  id: number;
  url: string;
  taskFailed?: boolean;
  taskRetry?: () => Promise<void>;
  attachmentsAttempted?: number;
  attachmentsFailed?: string[];
}

interface Props {
  onBack: () => void;
  onTicketCreated: (info: CreatedTicketInfo) => void;
  onError: (msg: string) => void;
}

const useStyles = makeStyles({
  root: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "4px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    marginTop: "8px",
  },
  contextHint: {
    color: tokens.colorNeutralForeground3,
  },
  submitRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  htmlPreview: {
    maxHeight: "180px",
    overflowY: "auto",
    padding: "8px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    fontSize: tokens.fontSizeBase300,
  },
});

export function CreateTicketForm({ onBack, onTicketCreated, onError }: Props): React.ReactElement {
  const styles = useStyles();

  // Contexte Outlook
  const [mail, setMail] = React.useState<MailContext | null>(null);
  const [mailError, setMailError] = React.useState<string | null>(null);

  // Champs form
  const [title, setTitle] = React.useState<string>("");
  const [description, setDescription] = React.useState<string>("");
  const [bodyHtml, setBodyHtml] = React.useState<string>("");
  const [useRichBody, setUseRichBody] = React.useState(true);
  const [type, setType] = React.useState<TicketType>(1);
  const [entityId, setEntityId] = React.useState<number | null>(null);
  const [categoryId, setCategoryId] = React.useState<number | null>(null);
  const [technicianId, setTechnicianId] = React.useState<number | null>(null);
  const [status, setStatus] = React.useState<TicketStatus>(1);
  const [statusManuallySet, setStatusManuallySet] = React.useState(false);
  const [requesters, setRequesters] = React.useState<GlpiUser[]>([]);
  const [autoRequesterDismissed, setAutoRequesterDismissed] = React.useState(false);
  const [task, setTask] = React.useState<GenericTaskValue>(defaultTaskValue(1));

  // Auto-détection demandeur
  const [autoFound, setAutoFound] = React.useState<GlpiUser | null | undefined>(undefined);
  const [autoLoading, setAutoLoading] = React.useState(false);

  // Pièces jointes : IDs sélectionnés (initialisés "toutes cochées" quand le mail arrive).
  const [selectedAttachmentIds, setSelectedAttachmentIds] = React.useState<string[]>([]);

  // Submission
  const [submitting, setSubmitting] = React.useState(false);
  const [submitStep, setSubmitStep] = React.useState<string>("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const [titleTouched, setTitleTouched] = React.useState(false);
  const [categoryTouched, setCategoryTouched] = React.useState(false);

  const { entities, loading: entLoading } = useEntities(true);
  const { categories, loading: catLoading } = useCategories(entityId);
  const { technicians, loading: techLoading } = useTechnicians(true);

  // 1) Lecture du mail au mount.
  React.useEffect(() => {
    let cancelled = false;
    getMailContext()
      .then(async (m) => {
        if (cancelled) return;
        setMail(m);
        setTitle(m.subject);
        const safeHtml = await prepareMailHtmlForGlpi(m.bodyHtml, m.attachments);
        if (cancelled) return;
        const plain = stripHtml(safeHtml || m.bodyText);
        setDescription(plain);
        setBodyHtml(safeHtml);
        setUseRichBody(safeHtml.length > 40);
        const defaultSelected = m.attachments.filter((a) => !a.isInline).map((a) => a.id);
        setSelectedAttachmentIds(defaultSelected);
      })
      .catch((e: Error) => {
        if (!cancelled) setMailError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 2) Auto-détection demandeur dès qu'on a l'email.
  React.useEffect(() => {
    if (!mail?.senderEmail) return;
    const client = getGlpiClient();
    if (!client) return;
    let cancelled = false;
    setAutoLoading(true);
    client
      .searchUserByEmail(mail.senderEmail)
      .then((users) => {
        if (cancelled) return;
        if (users.length === 1) {
          setAutoFound(users[0]);
          setRequesters((prev) => (prev.length === 0 && !autoRequesterDismissed ? [users[0]] : prev));
        } else if (users.length === 0) {
          setAutoFound(null);
        } else {
          // Plusieurs résultats : on laisse l'utilisateur choisir.
          setAutoFound(null);
        }
      })
      .catch(() => {
        if (!cancelled) setAutoFound(null);
      })
      .finally(() => {
        if (!cancelled) setAutoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mail?.senderEmail, autoRequesterDismissed]);

  // 3) Sélection auto de l'entité s'il n'y en a qu'une.
  React.useEffect(() => {
    if (entityId === null && entities.length === 1) {
      setEntityId(entities[0].id);
    }
  }, [entities, entityId]);

  // 4) Statut par défaut : Attribué (2) si technicien, sinon Nouveau (1).
  React.useEffect(() => {
    if (statusManuallySet) return;
    setStatus(technicianId ? 2 : 1);
  }, [technicianId, statusManuallySet]);

  function handleTechnicianChange(id: number | null): void {
    setTechnicianId(id);
  }

  function handleStatusChange(next: TicketStatus): void {
    setStatusManuallySet(true);
    setStatus(next);
  }

  function handleRequestersChange(next: GlpiUser[]): void {
    // Permet de supprimer le demandeur auto-détecté sans qu'il soit réinjecté.
    if (autoFound && requesters.length > 0 && next.length === 0) {
      setAutoRequesterDismissed(true);
    }
    setRequesters(next);
  }

  // Validation
  const titleError =
    titleTouched && !title.trim() ? "Titre obligatoire" : undefined;
  const entityError = entityId === null && !entLoading ? undefined : undefined;
  const categoryError =
    categoryTouched && categoryId === null && entityId !== null && !catLoading
      ? "Catégorie obligatoire"
      : undefined;
  const isValid =
    title.trim().length > 0 &&
    entityId !== null &&
    categoryId !== null &&
    requesters.length > 0;

  async function handleSubmit(): Promise<void> {
    setFormError(null);
    setTitleTouched(true);
    setCategoryTouched(true);

    if (!isValid) {
      setFormError("Complète au moins le titre, l'entité, la catégorie et un demandeur.");
      return;
    }

    const client = getGlpiClient();
    if (!client) {
      setFormError("Session GLPI absente. Recharge le task pane.");
      return;
    }

    const ticketContent = buildTicketBodyContent({
      useRichHtml: useRichBody,
      bodyHtml,
      bodyPlain: description,
      senderName: mail?.senderName ?? "",
      senderEmail: mail?.senderEmail ?? "",
    });

    if (!ticketContent.trim()) {
      setFormError("La description du ticket est vide.");
      return;
    }

    const payload: CreateTicketPayload = {
      name: title.trim(),
      content: ticketContent,
      entities_id: entityId as number,
      type,
      status,
      _users_id_requester: requesters.length === 1 ? requesters[0].id : requesters.map((u) => u.id),
    };
    payload.itilcategories_id = categoryId as number;
    if (technicianId !== null) payload._users_id_assign = technicianId;

    setSubmitting(true);
    setSubmitStep("Création du ticket…");
    try {
      const created = await client.createTicket(payload);
      const ticketId = Number(created.id);
      if (!Number.isFinite(ticketId)) {
        throw new Error("Réponse GLPI inattendue : pas d'ID.");
      }

      // Tâche générique (séquentielle, après ticket).
      let taskFailed = false;
      let taskRetry: (() => Promise<void>) | undefined;
      if (task.enabled) {
        const taskPayloads = buildTicketTaskPayloads(ticketId, task, technicianId);
        for (let i = 0; i < taskPayloads.length; i++) {
          setSubmitStep(`Création tâche ${i + 1}/${taskPayloads.length}…`);
          const payload = taskPayloads[i];
          try {
            await client.createTicketTask(ticketId, payload);
          } catch (e) {
            taskFailed = true;
            const c = client;
            taskRetry = async () => {
              for (const p of taskPayloads) {
                await c.createTicketTask(ticketId, p);
              }
            };
            // eslint-disable-next-line no-console
            console.warn("Création tâche échouée :", (e as Error).message);
            break;
          }
        }
      }

      // Upload des pièces jointes (séquentiel, après ticket et tâche).
      let attachmentsFailed: string[] = [];
      const toUpload = (mail?.attachments ?? []).filter((a) => selectedAttachmentIds.includes(a.id));
      if (toUpload.length > 0) {
        await client.ensureSession();
      }
      for (let i = 0; i < toUpload.length; i++) {
        const att = toUpload[i];
        setSubmitStep(`Upload pièce jointe ${i + 1}/${toUpload.length} : ${att.name}`);
        try {
          const fetched = await getAttachmentBlob(att.id, att);
          if (fetched.blob.size === 0) {
            throw new Error("Le fichier téléchargé depuis Outlook est vide (0 octet).");
          }
          await client.uploadDocumentToTicket(fetched.blob, fetched.name, ticketId, {
            ticketTitle: payload.name,
            entitiesId: entityId ?? undefined,
          });
        } catch (e) {
          const err = e as Error & { code?: string };
          const short =
            err.message && err.message.length < 200 && !err.message.includes("<!DOCTYPE")
              ? err.message
              : err.code === "GLPI_HTML_ERROR"
                ? "erreur serveur GLPI (voir console F12)"
                : err.code || "échec upload";
          attachmentsFailed.push(`${att.name} (${short})`);
          // eslint-disable-next-line no-console
          console.warn(`Upload PJ "${att.name}" échoué`, err.code ?? "", err.message, e);
        }
      }

      const url = buildTicketWebUrl(client.apiUrl, ticketId);
      onTicketCreated({
        id: ticketId,
        url,
        taskFailed,
        taskRetry,
        attachmentsAttempted: toUpload.length,
        attachmentsFailed,
      });
    } catch (e) {
      const err = e as Error;
      onError(err.message);
      setFormError(err.message);
    } finally {
      setSubmitting(false);
      setSubmitStep("");
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={onBack} aria-label="Retour">
          Retour
        </Button>
        <Body1><strong>Créer un ticket</strong></Body1>
      </div>

      <ErrorBar message={mailError} title="Lecture mail" />
      <ErrorBar message={formError} title="Validation" />

      <Field label="Titre" required validationState={titleError ? "error" : "none"} validationMessage={titleError}>
        <Input
          value={title}
          onBlur={() => setTitleTouched(true)}
          onChange={(_, d) => setTitle(d.value)}
          placeholder="Sujet du ticket"
        />
      </Field>

      <Field label="Type" required>
        <RadioGroup
          layout="horizontal"
          value={String(type)}
          onChange={(_, d) => setType((Number(d.value) === 2 ? 2 : 1) as TicketType)}
        >
          <Radio value="1" label="Incident" />
          <Radio value="2" label="Demande" />
        </RadioGroup>
      </Field>

      <EntityDropdown
        entities={entities}
        loading={entLoading}
        value={entityId}
        onChange={setEntityId}
        required
        error={entityError}
      />

      <CategoryDropdown
        categories={categories}
        loading={catLoading}
        value={categoryId}
        onChange={setCategoryId}
        disabled={entityId === null}
        required
        error={categoryError}
      />

      <RequesterPicker
        value={requesters}
        onChange={handleRequestersChange}
        senderEmail={mail?.senderEmail}
        autoFoundUser={autoFound}
        autoLoading={autoLoading}
        required
        error={requesters.length === 0 ? "Au moins un demandeur" : undefined}
      />

      <TechnicianDropdown
        technicians={technicians}
        loading={techLoading}
        value={technicianId}
        onChange={handleTechnicianChange}
      />

      <TicketStatusDropdown
        value={status}
        onChange={handleStatusChange}
        disabled={submitting}
      />

      <Field label="Description">
        {bodyHtml.length > 0 && (
          <Checkbox
            label="Conserver la mise en page du mail (HTML)"
            checked={useRichBody}
            onChange={(_, d) => setUseRichBody(!!d.checked)}
            style={{ marginBottom: 8 }}
          />
        )}
        {useRichBody && bodyHtml ? (
          <div
            className={styles.htmlPreview}
            dangerouslySetInnerHTML={{
              __html: buildTicketBodyContent({
                useRichHtml: true,
                bodyHtml,
                bodyPlain: description,
                senderName: mail?.senderName ?? "",
                senderEmail: mail?.senderEmail ?? "",
              }),
            }}
          />
        ) : (
          <Textarea
            resize="vertical"
            rows={6}
            value={description}
            onChange={(_, d) => setDescription(d.value)}
          />
        )}
        {useRichBody && bodyHtml && (
          <Caption1 style={{ color: tokens.colorNeutralForeground3, marginTop: 4 }}>
            Décochez « HTML » pour éditer en texte brut.
          </Caption1>
        )}
      </Field>

      <GenericTaskBlock ticketType={type} value={task} onChange={setTask} />

      {mail && mail.attachments.length > 0 && (
        <AttachmentsBlock
          attachments={mail.attachments}
          selected={selectedAttachmentIds}
          onChange={setSelectedAttachmentIds}
        />
      )}

      <Caption1 className={styles.contextHint}>
        Statut envoyé à GLPI : {TICKET_STATUS_LABELS[status]}
      </Caption1>

      {submitting && submitStep && (
        <div className={styles.submitRow}>
          <Spinner size="tiny" />
          <span>{submitStep}</span>
        </div>
      )}

      <div className={styles.footer}>
        <Button onClick={onBack} disabled={submitting}>
          Annuler
        </Button>
        <Button
          appearance="primary"
          icon={submitting ? <Spinner size="tiny" /> : <Send24Filled />}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Création…" : "Créer le ticket"}
        </Button>
      </div>
    </div>
  );
}
